import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Chip, Paper, Button } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import supabase from '../../supabaseClient';
import {
  PRODUCTION_SOURCES,
  fetchProductionLogs,
  sumQuantityForDay,
  computeAveragePerHour,
  computeWorkMinutes,
  evaluateBonusThresholds,
} from '../../Utils/machineProductionStats';

dayjs.extend(isoWeek);

const WEEKDAYS_PL = ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'];
const TRACK_BG = '#EDE7DA';
const HISTORY_FILL = '#C9C0AC';
const HISTORY_BADGE = '#87806E';
const TODAY_FILL = '#2e7d32';
const TODAY_BADGE = '#2e7d32';
const NORMA_COLOR = '#6B1D2F';
const CHART_HEIGHT = 210;

const fmtNumber = (n) => (n === null || n === undefined ? '—' : n.toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtTick = (n) => Math.round(n).toLocaleString('pl-PL');

// Wiersz statystyki w panelu produkcji: mały opis nad dużą wartością — ten sam
// wzorzec dla wszystkich "cyfr pod sobą" (produkcja, norma, próg…)
function StatRow({ label, value, unit, size = 'md', color }) {
  const fontSize = size === 'lg' ? 34 : 22;
  return (
    <Box>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#999', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize, fontWeight: 800, color: color || '#333', lineHeight: 1.15 }}>
        {value}
        {unit && (
          <Typography component="span" sx={{ fontSize: fontSize * 0.42, fontWeight: 600, color: '#999', ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

const fmtWorkTime = (minutes) => {
  const total = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

// activeWorkers to tablice stringów "Imię Nazwisko (Stanowisko)" — wyciągamy pierwszego
// dla nagłówka "Imię Nazwisko · Stanowisko"
function parsePrimaryWorker(activeWorkers) {
  if (!activeWorkers || activeWorkers.length === 0) return null;
  const match = activeWorkers[0].match(/^(.*) \((.*)\)$/);
  if (!match) return { name: activeWorkers[0], stand: '' };
  return { name: match[1], stand: match[2] };
}

function MachineProductionStats({ deviceId, idConfiguration, deviceName, clock, activeWorkers, selectedDate, onSelectDate }) {
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [thresholds, setThresholds] = useState([]);
  const [logs, setLogs] = useState([]);

  const todayStr = dayjs().format('YYYY-MM-DD');
  const isToday = selectedDate === todayStr;

  // Konfiguracja maszyny (norma, jednostka, źródło danych) + progi premiowe
  useEffect(() => {
    if (!deviceId || !idConfiguration) return;

    supabase.from('devices').select('daily_target, unit, production_source').eq('id', deviceId).maybeSingle()
      .then(({ data }) => setDeviceConfig(data || null));

    supabase.from('device_bonus_thresholds')
      .select('id, name, value_from, value_to, coefficient')
      .eq('device_id', deviceId).eq('id_configuration', idConfiguration)
      .order('value_from', { ascending: true })
      .then(({ data }) => setThresholds(data || []));
  }, [deviceId, idConfiguration]);

  const source = deviceConfig?.production_source ? PRODUCTION_SOURCES[deviceConfig.production_source] : null;

  // Logi produkcji dla okna 3 dni kończącego się na selectedDate — pobierane raz na
  // starcie, a potem odświeżane na żywo przez Supabase Realtime (INSERT do tabeli
  // źródłowej), z okresowym fallbackiem na wypadek cichego rozłączenia WebSocketu.
  useEffect(() => {
    if (!deviceId || !idConfiguration || !source) { setLogs([]); return; }

    let cancelled = false;
    // okno musi pokrywać zarówno 3-dniowy wykres, jak i cały tydzień ISO zawierający
    // selectedDate (do liczenia sumy "od poniedziałku" względem normy tygodniowej)
    const threeDayBackStr = dayjs(selectedDate).subtract(2, 'day').format('YYYY-MM-DD');
    const weekStartStr = dayjs(selectedDate).startOf('isoWeek').format('YYYY-MM-DD');
    const fromDateStr = weekStartStr < threeDayBackStr ? weekStartStr : threeDayBackStr;

    const reload = () => {
      fetchProductionLogs(supabase, deviceConfig.production_source, idConfiguration, fromDateStr, selectedDate)
        .then((rows) => { if (!cancelled) setLogs(rows); });
    };

    reload();

    const channel = supabase
      .channel(`production-${source.table}-${deviceId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: source.table,
        filter: `id_configuration=eq.${idConfiguration}`,
      }, (payload) => {
        const ts = payload.new?.[source.timestampColumn];
        const from = dayjs(fromDateStr).startOf('day');
        const to = dayjs(selectedDate).endOf('day');
        if (ts && !dayjs(ts).isBefore(from) && !dayjs(ts).isAfter(to)) {
          reload();
        }
      })
      .subscribe();

    const fallbackInterval = setInterval(reload, 60000);

    return () => {
      cancelled = true;
      clearInterval(fallbackInterval);
      supabase.removeChannel(channel);
    };
  }, [deviceId, idConfiguration, selectedDate, source, deviceConfig?.production_source]);

  const dayDates = useMemo(
    () => [2, 1, 0].map((offset) => dayjs(selectedDate).subtract(offset, 'day').format('YYYY-MM-DD')),
    [selectedDate]
  );

  const dayTotals = useMemo(() => {
    if (!source) return [0, 0, 0];
    return dayDates.map((d) => sumQuantityForDay(logs, source.valueColumn, source.timestampColumn, d));
  }, [logs, source, dayDates]);

  const selectedTotal = dayTotals[2];

  const averagePerHour = useMemo(() => {
    if (!source) return null;
    const dayLogs = logs.filter((l) => dayjs(l[source.timestampColumn]).format('YYYY-MM-DD') === selectedDate);
    return computeAveragePerHour(dayLogs, source.valueColumn, source.timestampColumn);
  }, [logs, source, selectedDate]);

  const workMinutes = useMemo(() => {
    if (!source) return 0;
    return computeWorkMinutes(logs, source.timestampColumn, selectedDate, new Date(), isToday);
  }, [logs, source, selectedDate, isToday]);

  // Suma produkcji od poniedziałku tygodnia zawierającego selectedDate do
  // selectedDate włącznie — norma (daily_target w bazie) jest teraz tygodniowa,
  // więc porównujemy ją z tą sumą, nie z produkcją samego wybranego dnia.
  const weekDates = useMemo(() => {
    const start = dayjs(selectedDate).startOf('isoWeek');
    const count = dayjs(selectedDate).diff(start, 'day') + 1;
    return Array.from({ length: count }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
  }, [selectedDate]);

  const weekToDateQuantity = useMemo(() => {
    if (!source) return 0;
    return weekDates.reduce((sum, d) => sum + sumQuantityForDay(logs, source.valueColumn, source.timestampColumn, d), 0);
  }, [logs, source, weekDates]);

  const thresholdStatus = useMemo(() => evaluateBonusThresholds(weekToDateQuantity, thresholds), [weekToDateQuantity, thresholds]);

  const weeklyTarget = deviceConfig?.daily_target != null ? Number(deviceConfig.daily_target) : null;
  const unit = deviceConfig?.unit || '';
  const diffToWeekNorm = weeklyTarget ? weeklyTarget - weekToDateQuantity : null;

  // Skala wykresu jest STAŁA (nie skacze dzień do dnia) — sięga najwyższego
  // progu premiowego; bez progów opieramy się o same wartości dni (norma jest
  // teraz tygodniowa, więc nie wyznacza już skali/linii na wykresie dziennym).
  const thresholdTops = useMemo(
    () => thresholds.map((t) => t.value_to ?? t.value_from).filter((v) => v != null),
    [thresholds]
  );
  // "Od" progu jest zawsze sensowną, skończoną wartością — w przeciwieństwie do "Do",
  // które w bazie jest NOT NULL i dla progu bez górnego limitu bywa wpisywane jako
  // bardzo duża liczba (np. 999999). Skala wykresu bazuje więc na realnych danych
  // (produkcja dni) i dolnych granicach progów; górną granicę progu uwzględniamy
  // w skali tylko gdy sama w sobie jest rozsądna (nieznacznie większa niż te dane) —
  // inaczej jeden otwarty próg rozjeżdżałby cały wykres do nieczytelności.
  const thresholdFroms = useMemo(
    () => thresholds.map((t) => t.value_from).filter((v) => v != null),
    [thresholds]
  );
  const maxScale = useMemo(() => {
    const dataCandidates = [...dayTotals, weeklyTarget, ...thresholdFroms].filter((v) => v != null && v > 0);
    const dataMax = dataCandidates.length > 0 ? Math.max(...dataCandidates) : 10;
    const reasonableTops = thresholdTops.filter((v) => v <= dataMax * 3);
    const topCandidate = reasonableTops.length > 0 ? Math.max(...reasonableTops) : dataMax;
    return Math.max(topCandidate, dataMax) * 1.3;
  }, [thresholdTops, thresholdFroms, dayTotals, weeklyTarget]);

  // Punkty odniesienia na osi: 0, granice progów premiowych (tylko te mieszczące się
  // w skali — otwarty górny próg poza maxScale nie dostaje własnej etykiety), max skali.
  const axisTicks = useMemo(() => {
    const raw = [0, ...thresholdTops.filter((v) => v <= maxScale), maxScale].filter((v) => v != null);
    const unique = Array.from(new Set(raw.map((v) => Math.round(v * 100) / 100))).sort((a, b) => a - b);
    return unique;
  }, [thresholdTops, maxScale]);

  const goPrevDay = () => onSelectDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD'));
  const goNextDay = () => onSelectDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD'));
  const goToday = () => onSelectDate(todayStr);

  const primaryWorker = parsePrimaryWorker(activeWorkers);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Nagłówek */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            {deviceName || 'Stanowisko maszynowe'}
          </Typography>
          {primaryWorker && (
            <Typography variant="body2" sx={{ color: '#888' }}>
              {primaryWorker.name}{primaryWorker.stand ? ` · ${primaryWorker.stand}` : ''}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 34, fontWeight: 300, color: '#444', letterSpacing: 1 }}>
          {clock}
        </Typography>
      </Box>

      {/* Nawigator dat */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#fff', borderRadius: 2, border: '1px solid #ECE7DE', px: 1, py: 0.5, mb: 1.5,
      }}>
        <IconButton size="small" onClick={goPrevDay}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>
            {isToday && 'dziś · '}{WEEKDAYS_PL[dayjs(selectedDate).day()]}, {dayjs(selectedDate).format('DD.MM')}
          </Typography>
          {isToday && (
            <Typography variant="caption" sx={{ color: '#999' }}>zmiana w toku</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={goNextDay} disabled={isToday}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          variant={isToday ? 'text' : 'outlined'}
          color="error"
          disabled={isToday}
          onClick={goToday}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, ml: 1 }}
        >
          DZIŚ
        </Button>
      </Box>

      {!source ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#FAF7F2', border: '1px solid #ECE7DE', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Brak skonfigurowanego źródła danych produkcji dla tej maszyny.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{
          display: 'flex', gap: 2.5, p: 2, bgcolor: '#fff', borderRadius: 3,
          border: '1px solid #ECE7DE',
        }}>
          {/* Wykres — własny "pigułkowy" słupek zamiast generycznego BarChart */}
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: CHART_HEIGHT }}>
              {dayDates.map((d, i) => {
                const value = dayTotals[i];
                const isTodayBar = i === 2 && isToday;
                const fillPct = Math.min(100, (value / maxScale) * 100);
                const label = isTodayBar ? 'dziś' : dayjs(d).format('DD.MM');
                return (
                  <Box key={d} sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: isTodayBar ? 70 : 40, height: '100%',
                  }}>
                    <Box sx={{
                      position: 'relative', width: '100%', flex: 1,
                      bgcolor: TRACK_BG, borderRadius: '20px', overflow: 'hidden',
                    }}>
                      {value > 0 && (
                        <Box sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: `${fillPct}%`,
                          bgcolor: isTodayBar ? TODAY_FILL : HISTORY_FILL,
                          borderTopLeftRadius: 4, borderTopRightRadius: 4,
                          borderBottomLeftRadius: 20, borderBottomRightRadius: 20,
                        }} />
                      )}
                      {value > 0 && (
                        <Box sx={{
                          position: 'absolute', left: '50%',
                          bottom: `${fillPct}%`, transform: 'translate(-50%, 50%)',
                          bgcolor: isTodayBar ? TODAY_BADGE : HISTORY_BADGE,
                          color: '#fff', fontSize: 12, fontWeight: 700,
                          px: 1, py: 0.3, borderRadius: 1.5, whiteSpace: 'nowrap',
                        }}>
                          {fmtNumber(value)}
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ mt: 0.75, fontSize: 12, color: '#999', fontWeight: isTodayBar ? 700 : 400 }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Oś z zakresami premiowymi (norma + granice progów) */}
            <Box sx={{ position: 'relative', width: 76, height: CHART_HEIGHT, ml: 1 }}>
              {axisTicks.map((t) => (
                <Typography key={t} sx={{
                  position: 'absolute', right: 0,
                  top: `${100 - Math.min(100, (t / maxScale) * 100)}%`, transform: 'translateY(-50%)',
                  fontSize: 11, whiteSpace: 'nowrap', color: '#999',
                }}>
                  {`${fmtTick(t)} ${unit}`}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Panel produkcji — kilka cyfr pod sobą: duża wartość + mały opis nad nią */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <StatRow
              label={isToday ? 'PRODUKCJA DZIŚ' : `PRODUKCJA · ${dayjs(selectedDate).format('DD.MM')}`}
              value={fmtNumber(selectedTotal)}
              unit={unit}
              size="lg"
              color={TODAY_FILL}
            />

            {weeklyTarget != null && (
              <StatRow
                label="NORMA TYGODNIOWA"
                value={`${fmtNumber(weekToDateQuantity)} / ${fmtNumber(weeklyTarget)}`}
                unit={unit}
              />
            )}

            {diffToWeekNorm != null && diffToWeekNorm !== 0 && (
              <StatRow
                label={diffToWeekNorm > 0 ? 'ZOSTAŁO DO PREMII' : 'PRZEKROCZONO O'}
                value={fmtNumber(Math.abs(diffToWeekNorm))}
                unit={unit}
                color={diffToWeekNorm < 0 ? TODAY_FILL : undefined}
              />
            )}

            {thresholdStatus.reached && (
              <StatRow label="AKTUALNY PRÓG" value={thresholdStatus.reached.name} color={NORMA_COLOR} />
            )}

            {thresholdStatus.next && (
              <StatRow
                label={`DO PROGU ${thresholdStatus.next.name}`}
                value={fmtNumber(thresholdStatus.next.value_from - weekToDateQuantity)}
                unit={unit}
              />
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 1, p: 1.25, bgcolor: '#FAF7F2', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 700 }}>CZAS</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{fmtWorkTime(workMinutes)}</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.25, bgcolor: '#FAF7F2', borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: '#999', fontWeight: 700 }}>TEMPO</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                  {averagePerHour != null ? `${fmtNumber(averagePerHour)} ${unit}/h` : '—'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Aktywni pracownicy */}
      {activeWorkers.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {activeWorkers.map((workerInfo, i) => (
            <Chip key={i} label={workerInfo} size="small"
              sx={{ bgcolor: 'rgba(46,125,50,0.1)', color: '#2e7d32', fontWeight: 600, border: '1px solid rgba(46,125,50,0.3)' }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default MachineProductionStats;
